/**
 * Calendar export for the B2C tracking surface (PLA-480).
 *
 * Pure builders only — no `window`/`document` access — so the module stays
 * unit-testable and platform-agnostic. The web-only DOM work (Blob download,
 * `window.open`) lives in `AddToCalendarButton.web.tsx`.
 *
 * Two event shapes, one per service type. A driveby customer drives to the
 * Prüfstelle, so the hub is the venue. An HBS customer never goes there — a PA
 * collects the car from their door — so the venue is the customer's own
 * address and the title says so. Putting the hub address on an HBS event would
 * send the customer to a Prüfstelle they were never meant to visit.
 *
 * @module utils/calendar-export
 */

/** Minutes the event blocks out, measured from the booked slot. */
const EVENT_DURATION_MINUTES = 30

/** RHS of the iCalendar UID. Stable per order so re-downloads supersede. */
const UID_DOMAIN = "holdeineplakette.de"

const DRIVEBY_TITLE = "Prüftermin bei HolDeinePlakette.de"
const HBS_TITLE = "Abholung deines Autos – HolDeinePlakette.de"

/**
 * Reminders, in minutes before DTSTART. `.ics` only — the Google Calendar URL
 * API carries no reminder parameter, so those users get their own defaults.
 * The day-before nudge is the one that actually prevents a no-show.
 */
const REMINDER_MINUTES_BEFORE = [24 * 60, 2 * 60]

/**
 * Statuses in which the appointment still lies ahead for either service type.
 * Deliberately excludes `no_show` and `cancelled` — `numericStatus` collapses
 * `no_show` into driveby phase 1, so gating on the phase number would offer a
 * calendar entry for an appointment the customer already missed.
 */
const PRE_APPOINTMENT_STATUSES = new Set(["new", "confirmed", "backlog"])

/**
 * The extra statuses a **driveby** still counts as booked-and-waiting in.
 *
 * Source of truth is `DRIVEBY_STATUS_LABELS` in `models/Order.ts`: every status
 * it labels "Termin gebucht" is one where the customer has not arrived yet and
 * the export must stay available. Together with
 * `PRE_APPOINTMENT_STATUSES` this set reproduces that group exactly —
 * `calendar-export.test.ts` pins the two together so they cannot drift.
 *
 * Not shared with HBS: for a driveby, `at_hub` is the normal booked state
 * (nothing operational has happened) and `picking_up` / `pickup_due` are
 * bookkeeping statuses the customer never sees as movement. For HBS the same
 * statuses mean a PA is already collecting, or has collected, the car.
 *
 * The set is deliberately imported as literals rather than from the model: this
 * module stays free of the mobx-state-tree import so it can be unit tested as a
 * pure function.
 */
const DRIVEBY_PRE_APPOINTMENT_STATUSES = new Set(["at_hub", "picking_up", "pickup_due"])

export const ICS_FILE_NAME = "holdeineplakette-termin.ics"
export const ICS_MIME_TYPE = "text/calendar;charset=utf-8"

export interface CalendarOrderInput {
  id: string
  serviceType?: string | null
  status?: string | null
  /**
   * The HubSpot SLA ceiling, ISO-8601 with an explicit Berlin offset. Always
   * this field, never `projectedPickupAtLive`: a calendar entry is a snapshot
   * the customer never re-downloads, so it must be anchored to the stable
   * promise rather than to a live projection that drifts within the hour.
   */
  scheduledPickupTime?: string | null
  hubLocation?: { name?: string | null; address?: string | null } | null
  /**
   * HBS pickup address. Absent on the `sanitizeOrderForPublic` fallback path,
   * which strips every customer address.
   */
  customerAddress?: string | null
  /** Feeds SEQUENCE so a re-download updates rather than duplicates. */
  updatedAt?: Date | string | null
}

export interface CalendarEvent {
  uid: string
  sequence: number
  title: string
  description: string
  location?: string
  start: Date
  end: Date
}

function isDrivebyOrder(order: CalendarOrderInput): boolean {
  return order.serviceType === "driveby"
}

/**
 * Whether to offer the export at all.
 *
 * Pre-appointment only: once the car is in the bay or delivered, adding a past
 * appointment reads as broken. The elapsed-block check is a second line of
 * defence for orders ops has not yet moved to `no_show`.
 */
export function canExportToCalendar(
  order: CalendarOrderInput | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!order?.scheduledPickupTime) return false

  const startMs = Date.parse(order.scheduledPickupTime)
  if (Number.isNaN(startMs)) return false
  if (startMs + EVENT_DURATION_MINUTES * 60_000 <= now) return false

  const status = order.status ?? ""
  if (PRE_APPOINTMENT_STATUSES.has(status)) return true
  return isDrivebyOrder(order) && DRIVEBY_PRE_APPOINTMENT_STATUSES.has(status)
}

/**
 * SEQUENCE must be a non-negative integer that only grows for a given UID, so
 * that a re-download after a B2C reschedule supersedes the earlier event
 * instead of duplicating it. Whole minutes since the epoch keeps the value
 * inside the 32-bit range calendar clients assume, and `updatedAt` advances on
 * every mirror write.
 */
function toSequence(updatedAt: CalendarOrderInput["updatedAt"]): number {
  if (!updatedAt) return 0
  const ms = updatedAt instanceof Date ? updatedAt.getTime() : Date.parse(updatedAt)
  if (Number.isNaN(ms) || ms <= 0) return 0
  return Math.floor(ms / 60_000)
}

function buildDescription(
  hubLocation: CalendarOrderInput["hubLocation"],
  trackingUrl?: string | null,
): string {
  const lines: string[] = []
  const hubName = hubLocation?.name?.trim()
  const hubAddress = hubLocation?.address?.trim()

  if (hubAddress) {
    lines.push(`Prüfstelle: ${hubName ? `${hubName}, ${hubAddress}` : hubAddress}`)
  }
  if (trackingUrl) {
    lines.push(`Live-Tracking: ${trackingUrl}`)
  }
  return lines.join("\n")
}

/**
 * Build the platform-neutral event. Returns null when there is no usable slot.
 *
 * `location` is omitted entirely when the relevant address is unavailable — a
 * correct event with no venue beats no event at all, and it never falls back
 * to the hub for an HBS order.
 */
export function buildCalendarEvent(
  order: CalendarOrderInput | null | undefined,
  trackingUrl?: string | null,
): CalendarEvent | null {
  if (!order?.scheduledPickupTime) return null

  const startMs = Date.parse(order.scheduledPickupTime)
  if (Number.isNaN(startMs)) return null

  const driveby = isDrivebyOrder(order)
  const location = driveby
    ? order.hubLocation?.address?.trim() || undefined
    : order.customerAddress?.trim() || undefined

  return {
    uid: `order-${order.id}@${UID_DOMAIN}`,
    sequence: toSequence(order.updatedAt),
    title: driveby ? DRIVEBY_TITLE : HBS_TITLE,
    description: buildDescription(order.hubLocation, trackingUrl),
    location,
    start: new Date(startMs),
    end: new Date(startMs + EVENT_DURATION_MINUTES * 60_000),
  }
}

/** RFC 5545 §3.3.11 — escape TEXT values. Backslash first, or we double-escape. */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n")
}

function utf8Length(codePoint: number): number {
  if (codePoint < 0x80) return 1
  if (codePoint < 0x800) return 2
  if (codePoint < 0x10000) return 3
  return 4
}

/**
 * RFC 5545 §3.1 — content lines fold at 75 **octets**, not characters. German
 * umlauts are two bytes in UTF-8, so folding on string length would emit lines
 * that look legal to us and are over-long to a strict parser. Iterating by
 * code point also keeps a surrogate pair from being split across a fold.
 */
function foldLine(line: string): string {
  const MAX_OCTETS = 75
  const segments: string[] = []
  let current = ""
  let octets = 0

  for (const char of line) {
    const size = utf8Length(char.codePointAt(0) as number)
    // Continuation lines open with a space, which costs an octet of its own.
    const limit = segments.length === 0 ? MAX_OCTETS : MAX_OCTETS - 1
    if (octets + size > limit) {
      segments.push(current)
      current = ""
      octets = 0
    }
    current += char
    octets += size
  }
  segments.push(current)
  return segments.join("\r\n ")
}

/**
 * UTC basic format. `scheduledPickupTime` already carries an explicit Berlin
 * offset, so converting to UTC is lossless and spares us a VTIMEZONE block.
 */
function formatIcsDate(date: Date): string {
  return date
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z")
}

export function toIcsFile(event: CalendarEvent, dtstamp: Date = new Date()): string {
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//HolDeinePlakette//CarAssist//DE",
    "CALSCALE:GREGORIAN",
    // Outlook needs METHOD to treat the file as an appointment to accept
    // rather than as a bare calendar document.
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${escapeText(event.uid)}`,
    `SEQUENCE:${event.sequence}`,
    `DTSTAMP:${formatIcsDate(dtstamp)}`,
    `DTSTART:${formatIcsDate(event.start)}`,
    `DTEND:${formatIcsDate(event.end)}`,
    `SUMMARY:${escapeText(event.title)}`,
  ]

  if (event.location) lines.push(`LOCATION:${escapeText(event.location)}`)
  if (event.description) lines.push(`DESCRIPTION:${escapeText(event.description)}`)
  lines.push("STATUS:CONFIRMED")

  for (const minutes of REMINDER_MINUTES_BEFORE) {
    lines.push(
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      `DESCRIPTION:${escapeText(event.title)}`,
      `TRIGGER:-PT${minutes}M`,
      "END:VALARM",
    )
  }

  lines.push("END:VEVENT", "END:VCALENDAR")
  return `${lines.map(foldLine).join("\r\n")}\r\n`
}

/**
 * Google's template URL. It has no update mode, so a re-download after a
 * reschedule duplicates the event there — accepted; the `.ics` path carries
 * the UID/SEQUENCE that lets Apple and Outlook supersede instead.
 */
export function toGoogleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${formatIcsDate(event.start)}/${formatIcsDate(event.end)}`,
  })
  if (event.description) params.set("details", event.description)
  if (event.location) params.set("location", event.location)
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}
